import {
	ListboxButton as ReachListboxButton,
	ListboxInput as ReachListboxInput,
	ListboxList as ReachListboxList,
	ListboxOption as ReachListboxOption,
	ListboxPopover as ReachListboxPopover,
} from '@reach/listbox';
import '@reach/listbox/styles.css';
import { VisuallyHidden } from '@reach/visually-hidden';
import { map } from 'ramda';
import React, { ReactNode, forwardRef, isValidElement } from 'react';
import { ChangeHandler } from 'react-hook-form';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';

import Chevron from '@assets/vectors/chevron.inline.svg';

import theme from '@theme/FolxTheme';

export interface IListboxOption {
	label: string | number;
	value: string | number | null;
}

interface IListboxProps {
	options: IListboxOption[];
	name: string;
	className?: string;
	defaultOption?: string;
	disabled?: boolean;
	displayValues?: boolean;
	errorLabel?: string;
	error?: boolean;
	value?: string | number;
	onChange?: (value: string) => void | ChangeHandler;
	label?: string | ReactNode;
	labelType?: 'normal' | 'bold';
	portal?: boolean;
}

type Ref = HTMLInputElement;

const StyledListboxInput = styled(ReachListboxInput)<{ error: boolean }>`
	background: ${theme.colors.white};

	[data-reach-listbox-button] {
		border: 1px solid
			${({ error }) => (error ? theme.tints.darken40(theme.colors.red) : theme.colors.black)};
		box-sizing: border-box;
		cursor: pointer;
		font-size: ${({ theme: components }) => components.listbox.font.fontSize.mobile};
		line-height: ${({ theme: components }) => components.listbox.font.lineHeight};
		padding: ${({ theme: components }) => components.listbox.padding};
		width: 100%;

		@media ${theme.responsive.breakpoints.tablet} {
			font-size: ${({ theme: components }) => components.listbox.font.fontSize.tablet};
		}
	}

	[data-reach-listbox-arrow] {
		margin-left: ${theme.spacing.lg};
		height: 0.8rem;

		svg {
			height: 100%;
			width: auto;
		}
	}

	[data-reach-listbox-popover] {
		max-height: 40rem;
	}

	[data-reach-listbox-option] {
		white-space: normal;
		overflow-wrap: normal;
	}

	&:disabled {
		background-color: ${theme.colors.lightGray};
		color: ${theme.tints.lighten70(theme.colors.black)};

		[data-reach-listbox-arrow] {
			svg {
				fill: ${theme.tints.lighten70(theme.colors.black)};
			}
		}
	}
`;

const StyledOption = styled(ReachListboxOption)`
	cursor: pointer;
	padding: ${theme.spacing.sm};
	font-size: ${theme.fontSizes.body.md};
	line-height: ${theme.lineHeights.body.md};
`;
const TextLabel = styled.label`
	${BasicText} {
		margin-bottom: ${theme.spacing.sm};
	}
`;

const ErrorText = styled.span`
	color: ${({ theme: components }) => components.textInput.errorColor};
	font-size: ${({ theme: components }) => components.textInput.errorFontSize};
	line-height: ${({ theme: components }) => components.textInput.errorLineHeight};
`;

const Wrapper = styled.div`
	display: flex;
	flex-flow: column;
	gap: ${theme.spacing.xs};
	width: 100%;
`;

const Listbox = forwardRef<Ref, IListboxProps>(
	(
		{
			className,
			defaultOption,
			disabled = false,
			options,
			name,
			onChange,
			value,
			label,
			labelType = 'normal',
			portal = true,
			displayValues = false,
			errorLabel = '',
			error = false,
		},
		ref
	) => {
		const buildStateOptions = map(({ value, label }) => (
			<StyledOption key={value} value={value}>
				{displayValues ? (value !== 'default' ? value : label) : label}
			</StyledOption>
		));

		const labelId = `${name}`;

		return (
			<div className={className}>
				{label && (
					<TextLabel htmlFor={name} id={name}>
						{isValidElement(label) ? label : <BasicText variant="field">{label}</BasicText>}
					</TextLabel>
				)}
				<input
					disabled={disabled}
					name={name}
					ref={ref}
					value={value ?? defaultOption}
					onChange={(event) => (onChange ? onChange(event.target.value) : (() => {})())}
					hidden
				/>
				<VisuallyHidden id={labelId}>{defaultOption}</VisuallyHidden>
				<Wrapper>
					<StyledListboxInput
						error={error}
						disabled={disabled}
						value={value}
						onChange={onChange}
						aria-labelledby={labelId}>
						<ReachListboxButton arrow={<Chevron />} />
						<ReachListboxPopover portal={portal}>
							<ReachListboxList>
								<>{buildStateOptions(options)}</>
							</ReachListboxList>
						</ReachListboxPopover>
					</StyledListboxInput>
					{error && <ErrorText>{errorLabel}</ErrorText>}
				</Wrapper>
			</div>
		);
	}
);

Listbox.displayName = 'Listbox';

export default styled(Listbox)``;
